import * as React from 'react';
import { useAppConfigs } from '@config/config.hooks';
import { Navigate } from 'react-router-dom';
import { useIsLoggedIn } from '@gcl/react-core';
import { useGetProjectByProjectNoAsync } from '@project/project.queries';
import { useGetMyDataAsync } from '@user/user.queries';
import { TableContentLoader } from '@common/TableContentLoader';
import { IRolePermissionFlattened } from '@userProfile/permission/permission.models';
import { routes } from './routes.models';
import { useNavigate } from './routes.hooks';

export interface IProtectedRouteProps {
  checkIsAllowed?: () => boolean;
  element: React.ReactNode;
  isRequiredLogin?: boolean;
  checkPermission?: (permissions: IRolePermissionFlattened[]) => boolean;
}

export const ProtectedRoute: React.FunctionComponent<IProtectedRouteProps> = ({
  checkIsAllowed,
  element,
  isRequiredLogin = true,
  checkPermission,
}) => {
  const appConfigs = useAppConfigs();

  const isLoggedIn = useIsLoggedIn();
  const { navigate, projectNoFromUrl } = useNavigate();
  const url = new URL(window.location.href.replace(/#/g, '?'));
  const hashParamCode = url.searchParams.get('code');
  const hashParamSessionState = url.searchParams.get('session_state');
  const isLoggingIn = !!(hashParamCode && hashParamSessionState);

  const { data: project, isLoading: isLoadingProject } = useGetProjectByProjectNoAsync(projectNoFromUrl, false);

  const { data: myData, isLoading: isLoadingMyData } = useGetMyDataAsync({ projectId: project?.item?.id }, false);

  const isAllowed = checkIsAllowed && typeof checkIsAllowed === 'function' && checkIsAllowed();
  const hasPermissions = checkPermission ? checkPermission(appConfigs.configs.myPermissions ?? []) : true;

  // Check via custom checkIsAllowed method or useHasPermissions
  const hasAccessRight = isAllowed || hasPermissions;

  React.useEffect(() => {
    if (isLoggingIn) return;
    if (!isLoggedIn && isRequiredLogin) {
      navigate(routes.common.requireLogin.path);
    }
  }, [isLoggedIn, isLoggingIn]);

  if (
    (isLoadingProject && projectNoFromUrl && !project) ||
    isLoadingMyData ||
    (!isLoadingMyData &&
      projectNoFromUrl &&
      myData?.item?.permissions !== undefined &&
      appConfigs.configs.myPermissions === undefined)
  ) {
    return (
      <div className="gcl-px-md gcl-mx-xl gcl-my-md">
        <TableContentLoader />
      </div>
    );
  }

  return hasAccessRight ? <>{element}</> : <Navigate to={routes.common.noAccessRight.path} />;
};
